// src/components/crm/InvoiceFormModal.js

import React, { useState, useEffect, useMemo, useRef } from 'react'; // ifm-notes-imp-1a9f4c7e — useRef for the pre-mint ref + composer handle
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, addDoc, runTransaction, collection, serverTimestamp, query, orderBy, onSnapshot, deleteField, deleteDoc } from 'firebase/firestore'; // ifm-notes-imp-1a9f4c7e — deleteDoc for the discard guard
import { X, Trash2, Save, Wrench, Package, Search, Loader2, Plus, FileText, AlertTriangle, MessageSquare, ChevronUp, ChevronDown, CheckCircle } from 'lucide-react'; // ifm-notes-imp-1a9f4c7e; CheckCircle: h1s6-8b2f4d19
import { canSeeAllCustomers, canApplyDepositNow, canRecordPayment } from '../../utils/permissions'; // officecust-9a4d2c61; canApplyDepositNow: s0rolegate-4c8a1f63; canRecordPayment: h1inv-6d3a9f27
import { jobInvoiceMarker } from '../../utils/jobInvoiceGuard'; // jobinvguard-5e1b7c84
import { buildAddressString } from '../../utils/address'; // PROPERTIES v3
import { buildBillToFromProperty, buildBillToFromCustomer, normalizeBillTo } from '../../utils/billTo'; // billto-ifm-import-3b8f6c21
import NoteInclusionPicker from './NoteInclusionPicker'; // r1-3f2-ifm-import-7c3a9e21
import { buildNoteInclusions } from '../../utils/noteInclusions'; // r1-3f2-ifm-import-7c3a9e21
import { formatCurrency, parseLocalDate, formatLocalDate, INVOICE_ROW_DERIVED_KEYS, INVOICE_SERVER_OWNED_KEYS, getPaymentSyncWarning, stripFirestoreSentinels } from '../../utils/formatters'; // strip-d4f7b32a sentstrip-6d2c9f14 localstamp-3c8b1d90
// ifm-notes-imp-1a9f4c7e — reuse the InvoiceDetailsView Notes & Media pieces (used, not modified)
import useOrgNotes from '../../hooks/useOrgNotes';
import NoteComposer from './NoteComposer';
import NoteCard from './NoteCard';
// wppromote-6a3f1e94 — Seam 3 (Promote): append into Work Performed, mark on save.
import { appendToWorkPerformed, promotionsSurvived, markNotesPromoted } from '../../utils/promoteNote';
import { pickerItems } from '../../utils/catalogUi'; // catscreen-7a2c4e91 — pickers hide archived items
import * as lineIds from '../../utils/lineIds'; // lineidweb-9e4a2d17 — permanent per-line IDs + the one template function
import { normalizeImportedLine } from '../../utils/importedLine'; // impline-7b3d9e52
import CustomerCombobox from './CustomerCombobox'; // invpick-2b7e5d14
import LineDescriptionInput from '../documents/LineDescriptionInput'; // linetext-4d8b2e61 — Enter, indents and "- " bullets in line descriptions
import { lineTaxCheckOf, clearsLineTaxCheck, LINE_TAX_CHECK_TEXT } from '../../utils/lineTaxCheck'; // taxlines1-f2503245
import { dropStaleTaxParts } from '../../utils/taxPartsOnSave'; // taxlines2-3e0b16a4
// taxlines5-a4c81e37 — Stage S5: when the company's Settings › Taxes switch is ON (documentLineTax.js) this
// pop-up gets the tax picker, "Set tax for all lines", one row per tax part, the customer exemption banner and
// the engine's figures (the server's invoice rule). Switch off (every real company today): nothing changes.
import useDocumentLineTax from '../documents/useDocumentLineTax';
import LineTaxPicker, { FieldTaxChip, TaxChangeNote } from '../documents/LineTaxPicker';
import SetAllTaxStrip from '../documents/SetAllTaxStrip';
import TaxExemptionBanner from '../common/TaxExemptionBanner';
import ConfirmationModal from '../common/ConfirmationModal';
import { EXEMPTION_CHANGE_FIELDS } from '../../utils/taxExemptionUi';
import {
    withItemSalesTax, applyDocumentTaxSave, onModeRowLabel, taxChangeTitle, TAX_SETTINGS_LOADING, isOfficeTaxRole,
    exemptionBlockedReason, overlayFields,
} from '../../utils/documentLineTax';

const InvoiceFormModal = ({ closeModal, editingInvoice, currentUserProfile, companySettings, customers, user, services }) => {
    // localstamp-3c8b1d90 — `today` is derived INSIDE the component with formatLocalDate
    // so it is (a) recomputed every time the modal opens instead of frozen at the module
    // load that happened when the tab first opened, and (b) the LOCAL calendar day, not a
    // UTC-shifted one. This is the date stamped on a new invoice's createdAt.
    const today = formatLocalDate();
    const invoiceStatuses = ['Unpaid', 'Paid', 'Past Due', 'Draft'];
    
    const [taxOptions, setTaxOptions] = useState([]);
    const [isLoadingTax, setIsLoadingTax] = useState(false);
    const [taxCodesFetched, setTaxCodesFetched] = useState(false); // taxlines5-a4c81e37 — QuickBooks codes answered (or failed)
    // taxlines5-a4c81e37 — a convert button's carried "Charge tax on this document" (Settings › Taxes ON only; the
    // seed's own allow-list below doesn't hold it), the stored invoice as the tax rules see it, the confirm.
    const carriedExemptionOff = editingInvoice && !editingInvoice.id && editingInvoice.taxExemptionOff ? editingInvoice.taxExemptionOff : null;
    const [taxSource, setTaxSource] = useState(() => (editingInvoice?.id ? editingInvoice : (carriedExemptionOff ? { taxExemptionOff: carriedExemptionOff } : null)));
    const [taxConfirm, setTaxConfirm] = useState(false);

    const defaultLineItem = {
        serviceId: '', name: '', description: '', quantity: 1, price: 0,
        taxExempt: false, taxExemptionNumber: '', type: 'Service', taxCodeId: '', addToCatalog: false    
    };

    const [invoiceData, setInvoiceData] = useState({
        customerId: '', 
        customerName: '', 
        customerDetails: {}, 
        // PROPERTIES v3 — service location (separate from the bill-to customerDetails).
        propertyId: null,
        propertyName: null,
        serviceAddress: null,
        billTo: null, // billto-ifm-state-5d9a4e72 -- null/absent = bill the main customer (today's behaviour)
        title: 'Invoice', // Default title
        createdAt: today,
        dueDate: today,
        lineItems: [defaultLineItem],
        notes: '',
        workPerformed: '',
        status: 'Unpaid',
    });
    // PROPERTIES v3 — service-location dropdown value: '' unchosen | 'main' | property id.
    const [serviceLocation, setServiceLocation] = useState('');
    // billto-ifm-selstate-8c4b1f63 -- Bill To selector state (see billto-qfm-selstate-4a9c1e76).
    // billToTouched initializes TRUE when the incoming document already carries a bill-to.
    // editingInvoice is BOTH the edit-mode doc and the convert-mode seed object, so a
    // billTo arriving from a quote/job through the convert allowlist counts as touched
    // and the property default can never overwrite it.
    const [pendingThirdParty, setPendingThirdParty] = useState(false);
    const [billToTouched, setBillToTouched] = useState(() => !!normalizeBillTo(editingInvoice?.billTo));
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    // SEAM 1 — settle date used when status is set to Paid via the dropdown.
    const [paidDate, setPaidDate] = useState(today);
    // r1-3f2-ifm-state-2e6b4d18 — render-only note-inclusion picker state for the
    // single job-to-invoice path. Kept OUT of invoiceData on purpose: handleSave
    // spreads invoiceData into dataToSave, so anything here can never persist except
    // the two flat fields we write explicitly on save. Seeded from editingInvoice.noteGroups
    // (attached by handleGenerateInvoiceFromJob); everything is ticked by default.
    const [noteGroups, setNoteGroups] = useState([]);                            // job's customer-facing note groups (0 or 1)
    const [excludedNoteIds, setExcludedNoteIds] = useState(new Set());           // unticked note ids

    // ── Notes & Media (create-time parity with quotes) ─────────────────────────
    // ifm-notes-mint-2b8d5e6f — ONE org/path source, shared by the pre-mint effect
    // and the create write (mirrors QuoteFormModal r1-2c-qfm-path), so two hand-built
    // paths can never disagree. Optional chaining: undefined orgId hides the strip
    // and no-ops the save. NOTE: handleSave declares its own local orgId/invoicesPath
    // (legal inner-scope shadowing) — same values, kept to minimise the diff there.
    const orgId = currentUserProfile?.organizationId;
    const invoicesPath = `organizations/${orgId}/invoices`;
    // ifm-notes-mint-2b8d5e6f — pre-mint an invoice id ONLY when creating so the Notes
    // strip has something to attach to before the first save. doc(collection(...))
    // writes NOTHING to the network, so setInvoiceNumberOnCreate does not fire early
    // and NO invoice number is consumed by opening (and abandoning) the form. The
    // record is still written exactly once, at save, to this same ref. Idempotent per
    // new-invoice session (the guard never replaces a minted ref); cleared when editing
    // an existing invoice (nothing is minted, its notes behave exactly as before).
    const newInvoiceRefRef = useRef(null);
    // ifm-notes-mint-2b8d5e6f — handle on the Notes composer so handleSave can commit a
    // note staged (typed) there. Null when the strip is collapsed/unmounted → commit no-ops.
    const noteComposerRef = useRef(null);
    const [preallocInvoiceId, setPreallocInvoiceId] = useState(null);
    useEffect(() => {
        if (editingInvoice?.id) { newInvoiceRefRef.current = null; setPreallocInvoiceId(null); return; }
        if (!orgId) return; // no org yet → strip stays hidden, save no-ops
        if (newInvoiceRefRef.current) return; // already minted this session — never replace
        const ref = doc(collection(db, invoicesPath));
        newInvoiceRefRef.current = ref;
        setPreallocInvoiceId(ref.id);
    }, [editingInvoice?.id, orgId]);
    // ifm-notes-mint-2b8d5e6f — job-aware attach rule, IDENTICAL to InvoiceDetailsView:108-116,
    // mobile, and the server resolver: the linked job when there is one, else the invoice.
    // The pre-mint changes only WHEN the standalone invoice key becomes available, not WHAT it is.
    // invId = real id when editing, pre-minted id when creating a standalone invoice.
    const invId = editingInvoice?.id || preallocInvoiceId;
    const notesJobId = invoiceData.linkedJobId || editingInvoice?.linkedJobId || editingInvoice?.jobId || null;
    const notesAttachedKey = notesJobId ? `job:${notesJobId}` : (invId ? `invoice:${invId}` : null);
    const notesAttachedTo = notesJobId ? [{ type: 'job', id: notesJobId }] : (invId ? [{ type: 'invoice', id: invId }] : []);
    const [notesExpanded, setNotesExpanded] = useState(false);
    const [composerDirty, setComposerDirty] = useState(false);

    // wppromote-6a3f1e94 — Seam 3 (Promote). This modal is ALSO the post-job,
    // pre-invoice review screen: JobFormModal's "Generate Invoice" ->
    // handleGenerateInvoiceFromJob -> openInvoiceModal(seed) lands here in CREATE
    // mode, with the box already pre-filled from the job's own workPerformed.
    //
    // The notes on this strip are the JOB's notes when the invoice came from a job
    // (notesAttachedKey above), but the box being filled is the INVOICE's own
    // workPerformed — Seam 2's contract is that once created the invoice is its own
    // document. That is exactly why the marker records targetType 'invoice': the
    // same note read later on the JOB screen must not claim the job's box holds it.
    //
    // Promote works before the invoice exists because the marker is an update to the
    // NOTE doc (which already exists — it lives on the job), written only after the
    // invoice save commits below.
    const [pendingPromotions, setPendingPromotions] = useState([]);
    const prePromoteTextRef = useRef('');
    const handlePromoteNote = (note) => {
        if (pendingPromotions.length === 0) prePromoteTextRef.current = invoiceData.workPerformed || '';
        setInvoiceData(prev => ({ ...prev, workPerformed: appendToWorkPerformed(prev.workPerformed, note.text) }));
        setPendingPromotions(prev => (prev.includes(note.id) ? prev : [...prev, note.id]));
    };
    const { notes: invoiceNotes } = useOrgNotes({ orgId, attachedKey: notesAttachedKey });
    // ifm-notes-mint-2b8d5e6f — warn on browser refresh/close with staged (typed/photo)
    // content (React cleanup is skipped there, so this is the only signal).
    useEffect(() => {
        if (!composerDirty) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [composerDirty]);
    // ifm-notes-mint-2b8d5e6f — guarded close for X / Cancel, mirroring QuoteFormModal:161-181.
    // Only a NEW, standalone (no-job) invoice can strand notes on a pre-minted id; on confirm
    // DELETE the stranded POSTED notes (NoteCard's onNoteDeleted cascade removes their
    // photos/thumbnails/notesMedia). invoiceNotes is the live useOrgNotes list of POSTED docs
    // only — an in-flight/staged photo has no doc yet and is cleaned by NoteComposer's own
    // unmount, so it must NOT be deleted here (that would double-delete). A saved invoice's
    // real notes, and a job-keyed note (which lives on the real job), are never touched.
    const handleGuardedClose = async () => {
        const strandable = !editingInvoice?.id && !notesJobId && !!preallocInvoiceId;
        const hasStranded = strandable && invoiceNotes.length > 0;
        if ((composerDirty || hasStranded) &&
            !window.confirm('This invoice has unsaved notes or photos that will be discarded. Leave without saving?')) return;
        if (hasStranded) {
            try {
                await Promise.all(invoiceNotes.map(n => deleteDoc(doc(db, `organizations/${orgId}/notes`, n.id))));
            } catch (e) {
                console.warn('InvoiceFormModal: failed to discard stranded invoice notes', e);
            }
        }
        closeModal();
    };

    // ── Job Templates ──────────────────────────────────────────────────────────
    const [jobTemplates, setJobTemplates] = useState([]);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            snap => setJobTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [currentUserProfile?.organizationId]);

    // --- COMPUTED: Filter Customers ---
    // officecust-9a4d2c61 — WHO SEES WHICH CUSTOMERS in this picker (founder decision 2026-10-04):
    // Office Staff + every high-level role see all; Sales Consultants see their own. AND the
    // customer already on the form (a job's / estimate's customer, or the one being edited) is
    // ALWAYS in the list, so a seeded invoice can never open with "Select Customer…" — the bug
    // Office Staff hit creating an invoice from a job (same idea as JobFormModal's
    // ensureCurrentCustomer). If that customer is somehow not in the loaded list, it is read by id
    // (firestore.rules lets every staff member read any customer of their company).
    const currentCustomerId = invoiceData.customerId;
    const [fetchedCustomer, setFetchedCustomer] = useState(null);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId || !currentCustomerId || (customers || []).some(c => c.id === currentCustomerId)) return undefined;
        let cancelled = false;
        getDoc(doc(db, `organizations/${orgId}/customers`, currentCustomerId))
            .then(snap => { if (!cancelled && snap.exists()) setFetchedCustomer({ ...snap.data(), id: snap.id }); })
            .catch(err => console.warn('Could not load the form\'s customer by id', err));
        return () => { cancelled = true; };
    }, [currentCustomerId, customers, currentUserProfile?.organizationId]);
    const availableCustomers = useMemo(() => {
        if (!currentUserProfile) return [];
        const all = customers || [];
        const base = canSeeAllCustomers(currentUserProfile)
            ? all
            : all.filter(c => c.sales_person_id === currentUserProfile.id);
        if (!currentCustomerId || base.some(c => c.id === currentCustomerId)) return base;
        const current = all.find(c => c.id === currentCustomerId)
            || (fetchedCustomer && fetchedCustomer.id === currentCustomerId ? fetchedCustomer : null);
        return current ? [current, ...base] : base;
    }, [customers, currentUserProfile, currentCustomerId, fetchedCustomer]);

    // --- EFFECT: Fetch Tax Codes ---
    useEffect(() => {
        const fetchTaxCodes = async () => {
            if (!currentUserProfile?.organizationId) return;
            setIsLoadingTax(true);
            try {
                const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                const result = await getOptions();
                if (result.data?.taxCodes) setTaxOptions(result.data.taxCodes); 
            } catch (error) { console.error("Failed to load tax codes:", error); } 
            finally { setIsLoadingTax(false); setTaxCodesFetched(true); }
        };
        fetchTaxCodes();
    }, [currentUserProfile]);

    // --- EFFECT: Initialize Data ---
    useEffect(() => {
        if (editingInvoice) {
            const isExistingInvoice = !!editingInvoice.id;

            if (isExistingInvoice) {
                // Edit Mode
                const safeGetDateString = (dateField) => {
                    let dateObject = dateField && typeof dateField.toDate === 'function' ? dateField.toDate() : new Date(dateField || new Date());
                    return dateObject.toISOString().split('T')[0];
                };

                const lineItems = (editingInvoice.lineItems || []).map(item => ({
                    ...defaultLineItem, ...normalizeImportedLine(item), // impline-7b3d9e52 — imported lines store unitPrice
                    type: item.type || 'Service', taxCodeId: item.taxCodeId || ''
                }));

                setInvoiceData({
                    ...editingInvoice,
                    lineItems,
                    createdAt: safeGetDateString(editingInvoice.createdAt),
                    dueDate: safeGetDateString(editingInvoice.dueDate),
                    customerDetails: editingInvoice.customerDetails || {}
                });
                // SEAM 1 — seed the settle-date picker from a prior status-settle.
                setPaidDate(editingInvoice.settledAt ? safeGetDateString(editingInvoice.settledAt) : today);
            } else {
                // New / Convert Quote Mode
                const prefillId = editingInvoice.customerId;
                const matchedCustomer = customers?.find(c => c.id === prefillId);
                
                const derivedDetails = matchedCustomer ? {
                    name: matchedCustomer.name,
                    email: matchedCustomer.email || '',
                    phone: matchedCustomer.phone || '',
                    address: [matchedCustomer.address1, matchedCustomer.address2, matchedCustomer.city].filter(Boolean).join(', '),
                } : (editingInvoice?.customerName ? { name: editingInvoice?.customerName } : {}); // strip-f619d54c keep customerDetails.name in step with customerName when the source customer is not in the loaded list

                const newDueDate = new Date();
                newDueDate.setDate(newDueDate.getDate() + (companySettings?.paymentTerms ?? 30)); // SEAM 2 — nullish: 0 = due on receipt

                const incomingItems = editingInvoice.lineItems && editingInvoice.lineItems.length > 0 
                    ? editingInvoice.lineItems.map(item => ({
                        ...defaultLineItem, ...item,
                        type: item.type || 'Service', taxCodeId: item.taxCodeId || ''
                    }))
                    : [defaultLineItem];

                setInvoiceData({
                    customerId: prefillId || '',
                    customerName: matchedCustomer?.name || editingInvoice.customerName || '',
                    customerDetails: derivedDetails,
                    // PROPERTIES v3 — carry the service location from the converted job/quote.
                    propertyId: editingInvoice.propertyId || null,
                    propertyName: editingInvoice.propertyName || null,
                    serviceAddress: editingInvoice.serviceAddress || null,
                    // billto-ifm-convert-2a6d9e48 -- this branch is an explicit allowlist (see the
                    // linkedQuoteId comment below): without this line a quote/job bill-to dies at
                    // conversion. Normalized so a malformed block can't propagate.
                    billTo: normalizeBillTo(editingInvoice.billTo),
                    title: 'Invoice',
                    createdAt: today,
                    dueDate: formatLocalDate(newDueDate),
                    lineItems: incomingItems,
                    notes: editingInvoice.notes || '',
                    workPerformed: editingInvoice.workPerformed || '',
                    status: 'Unpaid',
                    // DEPOSITS SEAM 4 VERIFIER: r2-dep-link-9c4f1e77
                    // This branch is an explicit allowlist and handleSave persists
                    // `invoiceData`, not `editingInvoice` — so anything omitted here
                    // never reaches Firestore. createNewInvoiceFromSource has always
                    // built these three, and they were always dropped right here:
                    // linkedQuoteId had ZERO readers repo-wide and linkedJobId only
                    // ever persisted from the backend paths (completeJob /
                    // batchCreateInvoices). Carrying them through is what lets the
                    // applyDepositToInvoice trigger find the source estimate.
                    //
                    // It also repairs interim labor billing: CrmApp.js queries
                    // where('linkedJobId','==',jobId) to find hours already billed,
                    // which matched NOTHING, so every interim invoice re-billed all
                    // labor from hour zero. The query succeeded empty, so its catch
                    // never fired and the safety degrade never engaged.
                    linkedQuoteId: editingInvoice.linkedQuoteId || null,
                    linkedQuoteNumber: editingInvoice.linkedQuoteNumber || null, // linkquotenum-7c1f9a4d — Seam 2d: carry the readable estimate # through the allowlist so it persists (null on the job path)
                    linkedJobId: editingInvoice.linkedJobId || null,
                    linkedJobNumber: editingInvoice.linkedJobNumber || null,
                });
            }
        } else {
            // New Blank Invoice
            const newDueDate = new Date();
            newDueDate.setDate(newDueDate.getDate() + (companySettings?.paymentTerms ?? 30)); // SEAM 2 — nullish: 0 = due on receipt

            setInvoiceData({
                customerId: '', customerName: '', customerDetails: {},
                title: 'Invoice', createdAt: today, dueDate: formatLocalDate(newDueDate),
                lineItems: [defaultLineItem], notes: '', workPerformed: '', status: 'Unpaid',
            });
        }
        // PROPERTIES v3 — sync the service-location dropdown to the loaded invoice.
        setServiceLocation(editingInvoice?.propertyId ? editingInvoice.propertyId : (editingInvoice ? 'main' : ''));
        // r1-3f2-ifm-seed-5a1f8c93 — seed the note-inclusion picker (render-only).
        // Only the job-generate path attaches noteGroups; edit/convert/blank all seed
        // to [] → picker hidden, no inclusion fields written. Everything ticked = empty
        // exclusion sets. Inside the init effect so it re-runs when the seed changes.
        setNoteGroups(Array.isArray(editingInvoice?.noteGroups) ? editingInvoice.noteGroups : []);
        setExcludedNoteIds(new Set());
    }, [editingInvoice, companySettings?.paymentTerms, customers]);

    // ── Apply a template to the invoice ───────────────────────────────────────
    const applyTemplate = (template) => {
        if (!template) return;
        // lineidweb-9e4a2d17 — the one shared template function (target 'invoice'): quantity as stored,
        // Tax Exempt (tmplexempt-5c9d1f38) and the tax code carried — exactly what this block built
        // before — plus a fresh lineId per line.
        const processedItems = lineIds.templateLinesFor(template.lineItems, { target: 'invoice', newId: lineIds.newLineId });
        setInvoiceData(prev => ({
            ...prev,
            title: template.name || prev.title,
            workPerformed: [template.defaultNotes, prev.workPerformed].filter(Boolean).join('\n'),
            lineItems: processedItems.length > 0 ? processedItems : prev.lineItems,
        }));
        setShowTemplatePicker(false);
    };

    // --- HANDLERS ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setInvoiceData(prev => ({ ...prev, [name]: value }));
    };

    // r1-3f2-ifm-toggle-3f7d2b64 — picker toggle handlers (mirror the batch flow).
    const toggleNoteExcluded = (id) => setExcludedNoteIds(prev => {
        const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
    // legnotes9-6b4e8d12 — SEAM 9. toggleLegacyExcluded / excludedLegacyJobIds removed
    // with the picker's "Older job notes from this job" row.

    const handleCustomerChange = (e) => {
        const customerId = e.target.value;
        const customer = customers.find(c => c.id === customerId);
        const hasProperties = (customer?.properties || []).length > 0;
        setInvoiceData(prev => ({
            ...prev,
            customerId: customerId,
            customerName: customer ? customer.name : '',
            customerDetails: customer ? {
                name: customer.name, email: customer.email || '',
                phone: customer.phone || '',
                address: [customer.address1, customer.address2, customer.city].filter(Boolean).join(', '),
            } : {},
            // PROPERTIES v3 — reset service location whenever the customer changes.
            propertyId: null, propertyName: null, serviceAddress: null,
            billTo: null, // billto-ifm-custreset-9f2b6d31 -- bill-to never survives a customer switch
        }));
        setServiceLocation(hasProperties ? '' : 'main');
        setPendingThirdParty(false);
        setBillToTouched(false);
    };

    // PROPERTIES v3 — user picked a service location: '' unchosen | 'main' | property id.
    // Bill-to (customerDetails) is unchanged; this only sets the service-location fields.
    const handleServiceLocationChange = (e) => {
        const value = e.target.value;
        setServiceLocation(value);
        const customer = customers.find(c => c.id === invoiceData.customerId);
        const prop = (value && value !== 'main')
            ? (customer?.properties || []).find(p => p.id === value)
            : null;
        // billto-ifm-handlers-6a1e8c47 -- apply the property's default bill-to when the user
        // has not touched the Bill To selector; a propertyContact bill-to never outlives
        // its property. Blocks are built ONLY by the shared builder (src/utils/billTo.js).
        setInvoiceData(prev => {
            let billTo = prev.billTo;
            if (!billToTouched && !billToLocked) {
                const def = prop?.defaultBillTo;
                if (def?.mode === 'propertyContact') billTo = buildBillToFromProperty(prop, customer);
                else if (def?.mode === 'thirdParty') billTo = buildBillToFromCustomer(customers.find(c => c.id === def.thirdPartyCustomerId));
                else billTo = null;
            } else if (!billToLocked && billTo?.mode === 'propertyContact' && (!prop || prop.id !== billTo.propertyId)) {
                billTo = null;
            }
            return {
                ...prev,
                propertyId: prop ? prop.id : null,
                propertyName: prop ? (prop.name || '') : null,
                serviceAddress: prop ? (prop.addressCanonical || buildAddressString(prop)) : null,
                billTo,
            };
        });
        if (!billToTouched) setPendingThirdParty(false);
    };

    const handleBillToChoiceChange = (e) => {
        if (billToLocked) return;
        const choice = e.target.value;
        setBillToTouched(true);
        if (choice === 'thirdParty') {
            setPendingThirdParty(true);
            setInvoiceData(prev => ({ ...prev, billTo: prev.billTo?.mode === 'thirdParty' ? prev.billTo : null }));
            return;
        }
        setPendingThirdParty(false);
        if (choice === 'propertyContact') {
            const cust = customers.find(c => c.id === invoiceData.customerId);
            const p = (cust?.properties || []).find(x => x.id === invoiceData.propertyId);
            setInvoiceData(prev => ({ ...prev, billTo: buildBillToFromProperty(p, cust) }));
        } else {
            setInvoiceData(prev => ({ ...prev, billTo: null }));
        }
    };

    const handleBillToThirdPartyChange = (e) => {
        if (billToLocked) return;
        const third = customers.find(c => c.id === e.target.value);
        setBillToTouched(true);
        setPendingThirdParty(!third);
        setInvoiceData(prev => ({ ...prev, billTo: buildBillToFromCustomer(third) }));
    };

    const handleLineItemChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        const items = [...invoiceData.lineItems];
        const currentItem = { ...items[index] };
        
        if (type === 'checkbox') {
            currentItem[name] = checked;
            if (name === 'taxExempt' && !checked) currentItem.taxExemptionNumber = '';
        } else {
            currentItem[name] = (name === 'quantity' || name === 'price') ? (value === '' ? '' : parseFloat(value)) : value;
        }
        
        items[index] = currentItem;
        setInvoiceData(prev => ({ ...prev, lineItems: items }));
    };

    const handleServiceSelect = (index, serviceId) => {
        const service = services?.find(s => s.id === serviceId); 
        const items = [...invoiceData.lineItems];
        if (service) {
            items[index] = {
                ...items[index],
                serviceId: serviceId, name: service.name, description: service.description,
                price: service.price, type: service.type || 'Service', taxCodeId: service.taxCodeId || '', 
                quantity: 1, addToCatalog: false 
            };
        } else {
             items[index] = { ...items[index], serviceId: '', addToCatalog: false, taxCodeId: '' };
        }
        setInvoiceData(prev => ({ ...prev, lineItems: items }));
    };

    const addLineItem = () => setInvoiceData(prev => ({ ...prev, lineItems: [...(prev.lineItems || []), lineIds.newLine(defaultLineItem, lineIds.newLineId)] })); // lineidweb-9e4a2d17 — a fresh object with its own lineId
    const removeLineItem = (index) => setInvoiceData(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== index) }));

    // taxlines5-a4c81e37 — Settings › Taxes ON only; docTax.taxOn is false for every other company.
    const taxCustomer = availableCustomers.find((c) => c.id === invoiceData.customerId) || null;
    const docTax = useDocumentLineTax({
        orgId: currentUserProfile?.organizationId, companySettings, taxOptions, taxLoaded: taxCodesFetched, customer: taxCustomer,
        role: currentUserProfile?.role, lines: invoiceData.lineItems, source: taxSource, kind: 'invoice', currency: companySettings?.currency || 'USD',
    });
    const pickService = (index, serviceId) => {
        handleServiceSelect(index, serviceId);
        if (!docTax.ctx) return;
        const service = services?.find((sv) => sv.id === serviceId) || null; // re-picking replaces the old item's code
        setInvoiceData((prev) => ({ ...prev, lineItems: prev.lineItems.map((l, j) => (j === index ? withItemSalesTax(l, service, docTax.ctx) : l)) }));
    };

    const calculations = useMemo(() => {
        if (docTax.totals) { // taxlines5-a4c81e37 — the engine's figures; a paid / written-off invoice shows what it stores
            const locked = Number(invoiceData.amountPaid || 0) > 0 || editingInvoice?.status === 'Written Off' || editingInvoice?.writtenOff === true;
            if (locked && editingInvoice) return { subtotal: Number(editingInvoice.subtotal) || 0, taxAmount: Number(editingInvoice.taxAmount) || 0, total: Number(editingInvoice.total) || 0 };
            return { subtotal: docTax.totals.subtotal, taxAmount: docTax.totals.taxAmount, total: docTax.totals.total };
        }
        let subtotal = 0;
        let totalTaxAmount = 0;

        invoiceData.lineItems.forEach(item => {
            const qty = Number(item.quantity) || 0;
            const price = Number(item.price) || 0;
            const lineTotal = qty * price;
            
            subtotal += lineTotal;

            if (!item.taxExempt) {
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }
                let applicableRate = specificTax ? specificTax.Rate : (companySettings?.taxRate || 0); 
                totalTaxAmount += lineTotal * (applicableRate / 100);
            }
        });

        return { subtotal, taxAmount: totalTaxAmount, total: subtotal + totalTaxAmount };
    }, [invoiceData.lineItems, companySettings, taxOptions, docTax.totals, invoiceData.amountPaid, editingInvoice]);

    // qbpaysync-2d4b8e17 -- hoisted so handleSave (the settle/reversal decision) and the Seam B
    // banner share ONE guard instead of two copies that could drift. Simple presence-count of any
    // paymentHistory entry (INCLUDING a Seam A 'skipped' one) -- that is exactly what keeps the
    // reversal clear from ever deleting a real payment's mark.
    const _hadRealPayments = Array.isArray(invoiceData.paymentHistory) && invoiceData.paymentHistory.length > 0;
    // billto-ifm-lock-4e7c3a95 -- payment lock, client half (firestore.rules billToLockSafe is
    // the real gate). Once any payment exists (real paymentHistory entry OR a status-settle
    // amountPaid), the Bill To selector disables. Judged on the loaded doc -- the same
    // pre-write image the rules see.
    const billToLocked = _hadRealPayments || Number(invoiceData.amountPaid || 0) > 0;

    // invlock-6b2e8c14 -- MONEY LOCK, client half (firestore.rules moneyLockSafe is the real
    // gate, shipping last). Keys on MONEY CURRENTLY PAID (amountPaid > 0), NOT "any payment
    // ever" -- so a fully refunded invoice (server clamps amountPaid to ~0) unlocks and edits
    // like an unpaid one, exactly as the rule's moneyPaid() does. Deliberately narrower than
    // billToLocked (which also stays locked on paymentHistory after a refund). When locked, the
    // line-item + tax controls are disabled AND handleSave resends the stored money verbatim.
    // s0bwoff-f74fb923 — S0b: a written-off invoice (bad debt, set by Spatchy support) is frozen
    // the same way: amounts AND status. firestore.rules writtenOffLockSafe is the real gate.
    const isWrittenOffInvoice = editingInvoice?.status === 'Written Off' || editingInvoice?.writtenOff === true;
    const moneyLocked = Number(invoiceData.amountPaid || 0) > 0 || isWrittenOffInvoice;

    // creditlock-9f3a2c17 -- CREDIT LOCK, client half (firestore.rules creditLockSafe +
    // creditFlagLockSafe are the real gate; this ships dormant until the future Customer
    // Account Credits feature sets creditApplied). Judged on the loaded doc (editingInvoice --
    // the same pre-write image the rule reads). While a credit is applied the rule freezes
    // amountPaid/balanceDue and forbids a client marking the invoice Paid (or moving it OFF
    // Paid), but PERMITS other status transitions (e.g. Unpaid -> Past Due) so a credited
    // overdue invoice can still enter collections. So: withhold the 'Paid' option unless the
    // invoice is ALREADY Paid, in which case lock the select entirely; show a banner; and
    // handleSave resends amountPaid/balanceDue/creditApplied verbatim.
    const creditApplied = editingInvoice?.creditApplied === true;
    const creditStatusIsPaid = creditApplied && editingInvoice?.status === 'Paid';
    // acctcredit-7d1b9e42 -- Seam 7: a stored status not in invoiceStatuses is
    // server-derived (today only 'Partial', written by buildInvoicePaymentUpdate
    // from the payment ledger). Show it selected but DISABLE the select so an
    // operator can't hand-overwrite a ledger-derived state; on save it rides
    // through invoiceData verbatim (== stored -> passes moneyLockSafe).
    const storedStatus = editingInvoice?.status;
    const isServerDerivedStatus = !!storedStatus && !invoiceStatuses.includes(storedStatus);
    // h1inv-6d3a9f27 — marking Paid (or un-marking it) is office-only (Office Staff / Owner / Admin /
    // Manager; firestore.rules invoiceSettleWriteSafe is the real gate). Others never see Paid as a
    // choice, and can't move an invoice that is already Paid.
    const mayMarkPaid = canRecordPayment(currentUserProfile);
    const baseStatusOptions = (creditApplied && !creditStatusIsPaid) || !mayMarkPaid
        ? invoiceStatuses.filter(s => s !== 'Paid')
        : invoiceStatuses;
    // Always keep the current stored status representable so the controlled
    // <select> never renders blank/mismatched.
    const statusOptions = storedStatus && !baseStatusOptions.includes(storedStatus)
        ? [storedStatus, ...baseStatusOptions]
        : baseStatusOptions;
    // h1s6-8b2f4d19 — H1 Seam 6: with a payment recorded, the status follows the payments (the server
    // sets Paid / Partial), so the menu can't flip it.
    const statusSelectDisabled = creditStatusIsPaid || isServerDerivedStatus || (!mayMarkPaid && storedStatus === 'Paid') || _hadRealPayments; // h1inv-6d3a9f27
    // h1s6-8b2f4d19 — choosing Paid on an invoice that isn't Paid yet no longer settles it by status
    // (that left no payment record, nothing in QuickBooks, and any paid deposit unapplied). The save
    // keeps the current status (a new or Draft invoice saves as Unpaid) and the Record Payment form
    // opens next, so a real payment is recorded and posted to QuickBooks. An invoice ALREADY Paid by
    // status keeps today's behaviour (re-save, or set it back to Unpaid).
    const recordPaymentOnSave = invoiceData.status === 'Paid' && storedStatus !== 'Paid' && !_hadRealPayments && !isWrittenOffInvoice;
    const statusWhenPaying = storedStatus && storedStatus !== 'Paid' && storedStatus !== 'Draft' ? storedStatus : 'Unpaid';

    // --- SAVE LOGIC ---
    const handleSave = async (skipTaxConfirm = false) => { // taxlines5-a4c81e37 — skipTaxConfirm
        if (!invoiceData.customerId) { setError("Please select a customer."); return; }
        // PROPERTIES v3 — force a service-location choice when the customer has properties.
        const _selCustForLoc = customers.find(c => c.id === invoiceData.customerId);
        if ((_selCustForLoc?.properties || []).length > 0 && serviceLocation === '') {
            setError("This customer has multiple service locations. Please choose which one this invoice is for.");
            return;
        }
        // billto-ifm-guard-8b4a7e16 -- "Another customer..." chosen but nobody picked yet.
        if (pendingThirdParty && !invoiceData.billTo) {
            setError('Choose which customer to bill, or set Bill To back to Main customer.');
            return;
        }
        setError('');
        // h1inv-6d3a9f27 — Paid must add up. With payments already recorded, choosing Paid doesn't
        // settle the rest (that's Record Payment's job), so it would leave a Paid invoice with a
        // balance owing. Production 2026-10-14: no such invoice exists; keep it that way.
        if (invoiceData.status === 'Paid' && editingInvoice?.status !== 'Paid' && _hadRealPayments
            && Number(editingInvoice?.balanceDue ?? 0) > 0.01) {
            setError('This invoice already has payments recorded and still has a balance owing. Use Record Payment for the rest instead of setting it to Paid.');
            return;
        }
        // taxlines5-a4c81e37 — Settings › Taxes ON: wait for the tax settings; an older invoice whose tax a save
        // would change asks first. Never on a paid / written-off invoice (its amounts are locked).
        if (docTax.taxDocLoading && !moneyLocked) { setError(TAX_SETTINGS_LOADING); return; }
        if (skipTaxConfirm !== true && docTax.taxNote && !moneyLocked) { setTaxConfirm(true); return; }
        setIsSaving(true);

        const orgId = currentUserProfile.organizationId;
        const invoicesPath = `organizations/${orgId}/invoices`;
        const servicesPath = `organizations/${orgId}/services`;

        const processedLineItems = await Promise.all(invoiceData.lineItems.map(async (item) => {
            if (item.addToCatalog && !item.serviceId && item.name) {
                try {
                    const newServiceRef = await addDoc(collection(db, servicesPath), {
                        name: item.name, description: item.description || '', price: Number(item.price),
                        type: item.type || 'Service', unit: 'Unit', taxCodeId: item.taxCodeId || '', 
                        createdAt: serverTimestamp()
                    });
                    item.serviceId = newServiceRef.id;
                } catch (e) { console.error("Failed to save new catalog item:", e); }
            }
            
            let effectiveTaxRate = 0;
            let effectiveTaxName = 'Tax';

            if (!item.taxExempt) {
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }
                if (specificTax) {
                    effectiveTaxRate = specificTax.Rate;
                    effectiveTaxName = specificTax.Name;
                } else {
                    effectiveTaxRate = companySettings?.taxRate || 0;
                    effectiveTaxName = `Tax (${effectiveTaxRate}%)`; 
                }
            } else {
                effectiveTaxName = 'Exempt';
            }

            const { addToCatalog, ...cleanItem } = item;
            return {
                ...cleanItem,
                addToCatalog: false,
                taxSnapshot: { rate: effectiveTaxRate, name: effectiveTaxName }
            };
        }));

        const dataToSave = {
            ...invoiceData,
            // lineidweb-9e4a2d17 — every line saved with a unique lineId, EXCEPT on an invoice that may
            // never be given one (paid, written off, imported / QuickBooks history): its lines go out as
            // they are (and the money lock below resends the stored ones anyway).
            lineItems: lineIds.invoiceTakesLineIds(editingInvoice?.id || null, editingInvoice || {})
                ? lineIds.ensureLineIds(processedLineItems, lineIds.newLineId)
                : processedLineItems,
            subtotal: calculations.subtotal,
            taxAmount: calculations.taxAmount,
            total: calculations.total,
            createdAt: parseLocalDate(invoiceData.createdAt),
            dueDate: parseLocalDate(invoiceData.dueDate),
            updatedAt: serverTimestamp(),
            organizationId: orgId,
            // h1inv-6d3a9f27 — set once, at create. Editing used to overwrite it with the EDITOR's uid.
            ...(editingInvoice?.id ? {} : { createdBy: user.uid }),
        };

        // r1-3f2-ifm-save-8e4c1a75 — write the flat note-inclusion field the backend
        // reads (includedNoteIds). Only when the picker was shown (noteGroups present —
        // a single group on this path). On fetch failure noteGroups is empty, so we write
        // NOTHING and the backend includes everything (today's behaviour). Reuses
        // buildNoteInclusions for parity with the batch flow.
        //
        // legnotes9-6b4e8d12 — SEAM 9. `includeLegacyTechNotes` is no longer written.
        // It was always true (its source, hasLegacy, was permanently false after Seam 8),
        // and the backend gate reads `!== false`, so omitting it is identical. Invoices
        // created before this change still carry the field; the backend accepts and
        // ignores it.
        if (noteGroups.length > 0) {
            const inclusions = buildNoteInclusions(noteGroups, excludedNoteIds);
            const single = inclusions[noteGroups[0].jobId];
            dataToSave.includedNoteIds = single.includedNoteIds;
        }

        // SEAM 1: reconcile the money fields from the TARGET status on every save
        // for invoices with no real recorded payments. Setting status to Paid via
        // the dropdown settles the invoice in full (amountPaid = total, balanceDue
        // = 0) and stamps a settle date, WITHOUT a paymentHistory entry — so it
        // never pushes to QuickBooks (the lightweight path for owners tracking
        // payments outside Spatchy). Any other status reconciles back to unsettled
        // (amountPaid = 0, balanceDue = total, marker cleared).
        //
        // This is DETERMINISTIC — it does not read the prior settledViaStatus
        // marker, which lives on the editingInvoice prop (a point-in-time snapshot
        // that isn't guaranteed fresh across entry paths). So reversing a
        // status-settle always restores the full balance, and it self-heals an
        // invoice left in a half-reversed state. Invoices with real paymentHistory
        // are left untouched (settle/close those via Record Payment).
        // s0bwoff-f74fb923 — never on a written-off invoice: its "not Paid" branch would reset
        // balanceDue to the full total and put the bad debt back on the books (the rules refuse it).
        if (recordPaymentOnSave) dataToSave.status = statusWhenPaying; // h1s6-8b2f4d19 — the payment form sets Paid
        if (!_hadRealPayments && !isWrittenOffInvoice) {
            if (dataToSave.status === 'Paid') { // h1s6-8b2f4d19 — was invoiceData.status: the status actually saved
                dataToSave.amountPaid = calculations.total;
                dataToSave.balanceDue = 0;
                dataToSave.settledViaStatus = true;
                dataToSave.settledAt = paidDate;
                dataToSave.lastPaymentDate = paidDate;
                // qbpaysync-7c1a9f03 -- the status-settle records a payment QuickBooks never
                // hears about (this path never calls createQuickBooksPayment). Persist the same
                // top-level mark Seam A writes so one query spans both paths. Written whenever the
                // org is on QuickBooks; the imported/linked/never-pushed distinction is left to the
                // banner and is re-derivable from the invoice, so the reason stays generic. Set
                // before the derived-key strip and not in INVOICE_ROW_DERIVED_KEYS, so it survives.
                if (companySettings?.quickbooksConnected) {
                    dataToSave.qbPaymentSyncError = 'This invoice was marked paid in Spatchy (status set to Paid), which does not record the payment in QuickBooks.';
                    dataToSave.qbPaymentSyncErrorAt = new Date().toISOString();
                }
            } else {
                dataToSave.amountPaid = 0;
                dataToSave.balanceDue = calculations.total;
                dataToSave.settledViaStatus = false;
                dataToSave.settledAt = null;
                // qbpaysync-5e82d6b4 -- clear any status-settle mark on reversal. Safe by the
                // Seam A/B invariant: this branch runs ONLY when there are no real payments
                // (paymentHistory empty), and a Seam A mark ALWAYS coexists with a paymentHistory
                // entry (createQuickBooksPayment writes the mark and the entry in one update), so
                // this can never clear a real payment's mark. deleteField is a no-op if absent.
                // qbpaysync-f2a91c68 -- HOTFIX: only on an EDIT. deleteField() is illegal on the
                // create write below (setDoc without merge, :441) and throws, rejecting the whole
                // save; a brand-new invoice cannot carry a stale mark anyway, so skip the clear.
                if (editingInvoice?.id) {
                    dataToSave.qbPaymentSyncError = deleteField();
                    dataToSave.qbPaymentSyncErrorAt = deleteField();
                }
            }
        }

        // invlock-2e6c4b88 -- MONEY LOCK verbatim resend. On a paid invoice (moneyLocked)
        // the money UI is disabled, so the money fields SHOULD equal the stored ones -- but
        // handleSave rebuilds them (processedLineItems adds taxSnapshot/addToCatalog and
        // normalizes keys; calculations.* recompute the totals from LIVE tax settings). That
        // rebuild would fail the firestore.rules equality check on a legitimate notes-only
        // edit. So when locked, overwrite the recomputed money with the RAW stored image from
        // the editingInvoice prop -- byte-identical to what the rule reads on the stored doc.
        // editingInvoice.* is the untransformed Firestore array (the invoice-list mapper only
        // adds qbPushState/paidDate -- INVOICE_ROW_DERIVED_KEYS -- it never touches lineItems
        // or the totals). taxRate needs no override: it is never overwritten here and rides
        // through unchanged via ...invoiceData (present -> same value, absent -> key omitted,
        // so merge leaves the stored value). Guarded on !== undefined so a legacy invoice
        // missing a field is never written as `undefined` (which setDoc rejects) -- the merge
        // then simply leaves that stored field untouched, which also satisfies the rule.
        if (moneyLocked && editingInvoice) {
            if (editingInvoice.lineItems !== undefined) dataToSave.lineItems = editingInvoice.lineItems;
            if (editingInvoice.subtotal  !== undefined) dataToSave.subtotal  = editingInvoice.subtotal;
            if (editingInvoice.taxAmount !== undefined) dataToSave.taxAmount = editingInvoice.taxAmount;
            if (editingInvoice.total     !== undefined) dataToSave.total     = editingInvoice.total;
        }

        // CREDIT LOCK verbatim resend (client half; firestore.rules creditLockSafe /
        // creditFlagLockSafe are the real gate -- see the creditApplied guard above). On a
        // credited invoice _hadRealPayments is true (the credit writes a tagged paymentHistory
        // entry), so the status-settle block above never runs and these ride through via
        // ...invoiceData -- resend them explicitly from the stored image so a future refactor
        // can't regress the equality the rule checks. status is intentionally NOT pinned: the
        // rule permits non-Paid transitions and the select already withholds the Paid option.
        if (creditApplied && editingInvoice) {
            if (editingInvoice.amountPaid    !== undefined) dataToSave.amountPaid    = editingInvoice.amountPaid;
            if (editingInvoice.balanceDue    !== undefined) dataToSave.balanceDue    = editingInvoice.balanceDue;
            if (editingInvoice.creditApplied !== undefined) dataToSave.creditApplied = editingInvoice.creditApplied;
        }

        // Strip the invoice-list mapper's view-only derived fields (qbPushState,
        // paidDate) that rode in via ...editingInvoice -> ...invoiceData -> dataToSave.
        // Under setDoc(merge:true) omitting a key never deletes stored data, so this
        // is safe by construction; it also keeps the derived keys out of the object
        // handed back to closeModal. Single source of truth is INVOICE_ROW_DERIVED_KEYS. strip-e508c43b
        INVOICE_ROW_DERIVED_KEYS.forEach((key) => { delete dataToSave[key]; });
        // taxlines1-f2503245 — an office role (Owner / Admin / Manager / Office Staff) saving the invoice clears
        // "tax couldn't be checked against QuickBooks". Edits only (deleteField is illegal on a create).
        if (editingInvoice?.id && lineTaxCheckOf(editingInvoice) && clearsLineTaxCheck(currentUserProfile)) dataToSave.lineTaxCodeCheck = deleteField();
        // taxlines2-3e0b16a4 — this editor re-totals tax its own way, so an edit drops the stored tax parts
        // (they'd no longer add up). Not on a create; not on a paid invoice (money lock: stored figures resent).
        dropStaleTaxParts(dataToSave, { isEdit: !!editingInvoice?.id, retotals: !moneyLocked, deleteValue: deleteField() });
        // taxlines5-a4c81e37 — Settings › Taxes ON: the engine's figures from the lines being saved (stamped codes, each
        // line's tax label, tax parts, the exemption stamp); an office role's save clears "Added in the field — check tax".
        // Never under the money lock: a paid / written-off invoice's lines and amounts are resent exactly as stored.
        if (docTax.ctx && !moneyLocked) applyDocumentTaxSave(dataToSave, { kind: 'invoice', ctx: docTax.ctx, doc: taxSource, isEdit: !!editingInvoice?.id, isOfficeRole: isOfficeTaxRole(currentUserProfile?.role), deleteValue: deleteField() });
        // h1inv-6d3a9f27 — never send the server-owned fields (see INVOICE_SERVER_OWNED_KEYS), nor
        // createdBy on an edit; setDoc(merge) leaves the stored values exactly as they are.
        INVOICE_SERVER_OWNED_KEYS.forEach((key) => { delete dataToSave[key]; });
        if (editingInvoice?.id) delete dataToSave.createdBy;
        // taxlines5-a4c81e37 — a NEW invoice from a convert button keeps its source's switch-off (as the server's
        // job → invoice paths do); Settings › Taxes ON + exemptions on only (CrmApp only passes it then).
        if (docTax.ctx && !editingInvoice?.id && carriedExemptionOff) dataToSave.taxExemptionOff = carriedExemptionOff;

        try {
            let invoiceId = editingInvoice?.id;
            const wasCreated = !invoiceId; // jobinvguard-5e1b7c84 — only a NEW invoice can mark its job
            if (invoiceId) {
                const docRef = doc(db, invoicesPath, invoiceId);
                await setDoc(docRef, dataToSave, { merge: true });
            } else {
                // ifm-notes-savepath-3c7e9a1d — write to the id minted when the modal
                // opened so photos staged in the Notes strip already point at THIS
                // invoice. setDoc (no merge) on a fresh id is a plain create, identical
                // to the old inline doc(collection(...)); the numbering trigger still
                // fires here, exactly once. The `??` fallback covers the (unreachable in
                // create mode) case of no pre-minted ref, so behaviour never regresses.
                const newDocRef = newInvoiceRefRef.current ?? doc(collection(db, invoicesPath));
                invoiceId = newDocRef.id;
                await setDoc(newDocRef, { ...dataToSave, id: invoiceId });
                dataToSave.id = invoiceId;
            }

            // ifm-notes-commit-4d6f8b2e — commit any note typed into the Notes & Media
            // strip as part of this save (mirrors QuoteFormModal r1-2d). Runs AFTER the
            // invoice doc exists so a standalone note attaches to a real invoice id.
            // Best-effort: commit() is a no-op when the strip is empty/unmounted and
            // never throws, so a failed note can never fail the invoice save.
            try {
                await noteComposerRef.current?.commit();
            } catch (noteErr) {
                console.warn('InvoiceFormModal: staged note commit failed (invoice already saved):', noteErr);
            }

            // wppromote-6a3f1e94 — the invoice write has committed (both the create
            // and the edit branch above), so tag the notes whose text reached it.
            // targetType 'invoice' because the box that was filled is
            // invoice.workPerformed, even when the note itself lives on the linked
            // job — the chip's hover title says which, so the same note read later on
            // the JOB screen doesn't claim the job's box holds it. Pressing Cancel
            // instead of Save marks nothing: pendingPromotions dies with the modal.
            if (pendingPromotions.length > 0) {
                if (promotionsSurvived(prePromoteTextRef.current, invoiceData.workPerformed)) {
                    await markNotesPromoted({
                        orgId,
                        noteIds: pendingPromotions,
                        currentUserProfile,
                        targetType: 'invoice',
                    });
                }
                setPendingPromotions([]);
                prePromoteTextRef.current = '';
            }

            // --- SYNC INVOICE TOTAL BACK TO LINKED JOB ---
            // If this invoice is linked to a job, write the final invoiceTotal back
            // so that Job Profitability and Team Performance reports stay accurate.
            // We only update invoiceTotal — we never touch jobCosting.laborEntries
            // here because those are owned by the clock-in system.
            const linkedJobId = invoiceData.linkedJobId || editingInvoice?.linkedJobId;
            if (linkedJobId) {
                try {
                    const jobRef = doc(db, `organizations/${orgId}/jobs`, linkedJobId);
                    // s6costing-docid — updateDoc, NOT setDoc(merge): a wrong linkedJobId (e.g. a
                    // recurring template id that a stale `id` field leaked into the source job) used to
                    // CREATE a stub job doc at that id (jobs/cvphgARgoVdMLozny7Qx, 2026-08-11). An
                    // update can only touch a job that exists; a miss lands in the warn below.
                    // jobinvguard-5e1b7c84 — and when this save CREATED the invoice for a COMPLETED job
                    // that has no marker yet, set job.invoiceId: the job leaves Needs Invoicing
                    // (Completed && !invoiceId) and batchCreateInvoices' "Already invoiced" guard now
                    // covers it. A transaction reads the job's CURRENT status/marker so the decision is
                    // never made from a stale copy. Progress invoices on unfinished jobs, and every
                    // later invoice, leave the marker alone (jobInvoiceMarker). A missing job still
                    // throws inside the transaction and lands in the warn below, exactly like updateDoc.
                    const savedInvoiceId = invoiceId;
                    await runTransaction(db, async (tx) => {
                        const jobSnap = await tx.get(jobRef);
                        if (!jobSnap.exists()) throw new Error(`job ${linkedJobId} not found`);
                        const marker = wasCreated ? jobInvoiceMarker({ jobData: jobSnap.data(), newInvoiceId: savedInvoiceId }) : null;
                        tx.update(jobRef, {
                            invoiceTotal: calculations.total,
                            updatedAt: serverTimestamp(),
                            ...(marker || {}),
                        });
                    });
                } catch (jobSyncErr) {
                    // Don't fail the whole save if the job sync fails — just warn.
                    // The invoice is already saved correctly.
                    console.warn("Invoice saved, but could not sync invoiceTotal back to job:", jobSyncErr);
                }
            }
            // -----------------------------------------------

            // dep-apply-3c7f9a21 -- Deposit Remediation Seam 2a (STEP 4): on CREATE only,
            // apply any deposit sitting on the source estimate to this fresh invoice before
            // the modal closes. Fires only when a quote/job link is present (no link => no
            // deposit to find, so skip the call entirely). Sits inside the isSaving-true
            // region so the existing spinner covers it and the modal cannot close on
            // uncredited numbers. The invoice already exists here, and the retained
            // applyDepositToInvoice trigger applies the deposit regardless, so a callable
            // failure must NEVER surface as a save error -- its own try/catch swallows it and
            // we fall back to the original asynchronous behaviour.
            // s0rolegate-4c8a1f63 — S0: the callable is office-only now. A Sales Consultant skips it
            // (no refused call is ever sent); the applyDepositToInvoice trigger still applies the
            // deposit to their invoice moments later, exactly the fallback described above.
            if (!editingInvoice?.id && (dataToSave.linkedQuoteId || dataToSave.linkedJobId)
                && canApplyDepositNow(currentUserProfile)) {
                try {
                    const depRes = await httpsCallable(functions, 'applyDepositToInvoiceNow')({ invoiceId });
                    const applied = depRes?.data?.applied || 0;
                    if (applied > 0) {
                        // Correct the payload handed to the parent so the onward figures reflect the
                        // credit, not the uncredited amountPaid:0 / balanceDue:total written above.
                        // The callable returns { applied } only, so derive the balance here.
                        dataToSave.amountPaid = applied;
                        dataToSave.balanceDue = calculations.total - applied;
                    }
                } catch (_) { /* invoice already saved; the kept trigger nets the deposit -- never error a saved invoice */ }
            }

            setIsSaving(false);
            closeModal(stripFirestoreSentinels(dataToSave), recordPaymentOnSave ? { recordPayment: true } : undefined); // sentstrip-4a7e1b93 -- write kept sentinels above; state copy must not; h1s6-8b2f4d19 — open Record Payment next
        } catch (err) {
            console.error("Error saving invoice:", err);
            setError(err.message || "An unknown error occurred.");
            setIsSaving(false);
        }
    };
    
    const currencyCode = companySettings?.currency || 'USD';

    // qbpaysync-1f9c3a7d -- Seam B banner. The status-settle path never syncs, so warn whenever
    // the settle will fire (status Paid AND no real payments -- shared _hadRealPayments guard) and
    // the org is on QuickBooks. Mode 'settle' so a LINKED invoice still warns (opposite of the
    // Record Payment path). id is spread in edit mode; passing editingInvoice?.id also covers it
    // and yields undefined (never-pushed) in create mode.
    const settleSyncWarning = (invoiceData.status === 'Paid' && !_hadRealPayments && !recordPaymentOnSave) // h1s6-8b2f4d19
        ? getPaymentSyncWarning({ ...invoiceData, id: editingInvoice?.id }, companySettings?.quickbooksConnected, 'settle')
        : null;

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-0 md:p-4 backdrop-blur-sm">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                
                {/* --- HEADER --- */}
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{editingInvoice?.id ? 'Edit Invoice' : 'New Invoice'}</h2>
                        <p className="text-xs text-gray-500">{invoiceData.createdAt}</p>
                    </div>
                    <button onClick={handleGuardedClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors"> {/* ifm-notes-close-6f2b8d4a */}
                        <X size={20} className="text-gray-600" />
                    </button>
                </div>

                {/* --- BODY --- */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50/50">
                    
                    {/* DETAILS CARD */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* LEFT: Customer & Title */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer</label>
                                    {editingInvoice?.id ? (
                                        <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                            <p className="font-bold text-gray-900">{invoiceData.customerDetails?.name}</p>
                                            <p className="text-xs text-gray-500">{invoiceData.customerDetails?.email}</p>
                                        </div>
                                    ) : (
                                        // invpick-2b7e5d14 — the same type-to-filter picker the job and estimate forms
                                        // use (custpick-8e5a2c71). Same availableCustomers list; it emits the
                                        // { target: { name, value } } a <select> does, so handleCustomerChange runs as before.
                                        <CustomerCombobox
                                            name="customerId"
                                            customers={availableCustomers || []}
                                            value={invoiceData.customerId}
                                            onChange={handleCustomerChange}
                                        />
                                    )}
                                </div>
                                {/* PROPERTIES v3 — Service location selector (only when the customer has properties) */}
                                {(() => {
                                    const selCust = customers.find(c => c.id === invoiceData.customerId);
                                    const props = selCust?.properties || [];
                                    if (props.length === 0) return null;
                                    return (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                Service Location <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={serviceLocation}
                                                onChange={handleServiceLocationChange}
                                                className={`w-full p-2.5 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm ${serviceLocation === '' ? 'border-red-400' : ''}`}
                                            >
                                                <option value="">-- Choose a service location --</option>
                                                <option value="main">Main address{selCust ? ` — ${buildAddressString(selCust)}` : ''}</option>
                                                {props.map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {(p.name || p.address1 || 'Unnamed property')} — {p.addressCanonical || buildAddressString(p)}
                                                    </option>
                                                ))}
                                            </select>
                                            {serviceLocation === '' && (
                                                <p className="mt-1 text-xs text-red-500">This customer has multiple locations — choose which one this invoice is for.</p>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* billto-ifm-ui-3c9d5b82 -- Bill To selector. Phase 1 stores the choice only;
                                    PDFs, emails, QuickBooks and the portal still use the main customer. */}
                                {invoiceData.customerId && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bill To</label>
                                        <select
                                            value={pendingThirdParty ? 'thirdParty' : (invoiceData.billTo?.mode || 'main')}
                                            onChange={handleBillToChoiceChange}
                                            disabled={billToLocked}
                                            className="w-full p-2.5 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                        >
                                            <option value="main">Main customer</option>
                                            {invoiceData.propertyId ? <option value="propertyContact">Property contact</option> : null}
                                            <option value="thirdParty">Another customer...</option>
                                        </select>
                                        {billToLocked && (
                                            <p className="mt-1 text-xs text-gray-500">A payment has been applied, so the bill-to can no longer change.</p>
                                        )}
                                        {!billToLocked && (pendingThirdParty || invoiceData.billTo?.mode === 'thirdParty') && (
                                            <select
                                                value={invoiceData.billTo?.customerId || ''}
                                                onChange={handleBillToThirdPartyChange}
                                                className={`mt-1 w-full p-2.5 border rounded-lg bg-white text-sm ${!invoiceData.billTo ? 'border-red-400' : ''}`}
                                            >
                                                <option value="">-- Choose the customer to bill --</option>
                                                {customers.filter(c => c.id !== invoiceData.customerId).map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Invoice Title</label>
                                    <input 
                                        type="text" 
                                        name="title" 
                                        value={invoiceData.title} 
                                        onChange={handleChange} 
                                        className="w-full p-2 border rounded-lg text-sm" 
                                        placeholder="e.g. Lawn Maintenance"
                                    />
                                </div>
                            </div>
                            
                            {/* RIGHT: Dates & Status */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <input type="date" name="createdAt" value={invoiceData.createdAt} onChange={handleChange} className="w-full p-2 border rounded-lg text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Due</label>
                                    <input type="date" name="dueDate" value={invoiceData.dueDate} onChange={handleChange} className="w-full p-2 border rounded-lg text-sm" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                                    <select
                                        name="status"
                                        value={invoiceData.status}
                                        onChange={handleChange}
                                        disabled={statusSelectDisabled}
                                        className="w-full p-2 border rounded-lg text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                                {_hadRealPayments && !isServerDerivedStatus && (
                                    <div className="col-span-2 -mt-1 text-xs text-gray-500">
                                        {/* h1s6-8b2f4d19 */}
                                        Status is set automatically from recorded payments and can't be changed here.
                                    </div>
                                )}
                                {recordPaymentOnSave && (
                                    <div className="col-span-2 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
                                        {/* h1s6-8b2f4d19 */}
                                        <CheckCircle size={16} className="mt-0.5 shrink-0" />
                                        <span>To mark this invoice Paid, record the payment. When you save, the Record Payment form opens so you can enter the amount, method and date{companySettings?.quickbooksConnected ? ', and the payment is sent to QuickBooks' : ''}. The invoice shows Paid once the full balance is recorded.</span>
                                    </div>
                                )}
                                {isServerDerivedStatus && (
                                    <div className="col-span-2 -mt-1 text-xs text-gray-500">
                                        {isWrittenOffInvoice /* s0bwoff-f74fb923 */
                                            ? "This invoice was written off as bad debt by Spatchy support, so its status and amounts can't be changed here."
                                            : "Status is set automatically from recorded payments and can't be changed here."}
                                    </div>
                                )}
                                {creditApplied && (
                                    <div className="col-span-2 flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-800 text-sm">
                                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                        <span>Account credit has been applied to this invoice. Its balance is managed automatically, and it can't be marked Paid here — that happens automatically once the balance is cleared.</span>
                                    </div>
                                )}
                                {/* SEAM 1 — settle date, shown only when marking Paid. */}
                                {invoiceData.status === 'Paid' && !recordPaymentOnSave && ( /* h1s6-8b2f4d19 — only an invoice already Paid by status */
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date Paid</label>
                                        <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} className="w-full p-2 border rounded-lg text-sm" />
                                        <p className="text-xs text-gray-400 mt-1">Marks the invoice paid in full on this date. No online payment is taken.</p>
                                    </div>
                                )}
                                {settleSyncWarning && (
                                    <div className="col-span-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                        {/* qbpaysync-a6b0e451 */}
                                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                        <span>{settleSyncWarning}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── TEMPLATE PICKER ── only shown when creating new invoice ── */}
                    {!editingInvoice?.id && jobTemplates.length > 0 && (
                        <div className="mb-4">
                            {!showTemplatePicker ? (
                                <button
                                    type="button"
                                    onClick={() => setShowTemplatePicker(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 w-full border-2 border-dashed border-blue-200 rounded-xl text-sm text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all font-medium"
                                >
                                    <FileText size={15} className="flex-shrink-0" />
                                    Start from a template…
                                </button>
                            ) : (
                                <div className="border border-blue-200 rounded-xl overflow-hidden bg-blue-50">
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-blue-200">
                                        <span className="text-sm font-semibold text-blue-800">Choose a template</span>
                                        <button type="button" onClick={() => setShowTemplatePicker(false)} className="text-blue-400 hover:text-blue-600"><X size={16} /></button>
                                    </div>
                                    <div className="max-h-52 overflow-y-auto divide-y divide-blue-100">
                                        {jobTemplates.map(t => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => applyTemplate(t)}
                                                className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-blue-100 transition-colors"
                                            >
                                                <FileText size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-800 truncate">{t.name}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {t.lineItems?.length ? `${t.lineItems.length} line item${t.lineItems.length > 1 ? 's' : ''}` : 'No line items'}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* LINE ITEMS */}
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Line Items</h3>
                        {/* invlock-4d8b1e29 -- no adding line items to a paid invoice (the array is frozen). */}
                        <button onClick={addLineItem} disabled={moneyLocked} className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1 disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed">
                            <Plus size={16}/> Add Item
                        </button>
                    </div>

                    {/* taxlines1-f2503245 -- a line's QuickBooks tax code couldn't be checked when this invoice was made. */}
                    {lineTaxCheckOf(editingInvoice) && (
                        <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 text-sm" role="status" data-testid="line-tax-check-banner">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                            <span>{LINE_TAX_CHECK_TEXT}</span>
                        </div>
                    )}

                    {docTax.ctx && (
                        // taxlines5-a4c81e37 — the customer's exemption (blue applied / amber switched off) and "Set tax for all lines".
                        <div className="mb-4 space-y-3">
                            <TaxExemptionBanner kind="invoice" orgId={currentUserProfile?.organizationId} currentUserProfile={currentUserProfile} customer={taxCustomer}
                                document={{ ...(taxSource || {}), id: taxSource?.id, taxAmount: calculations.taxAmount, taxParts: moneyLocked ? taxSource?.taxParts : docTax.result.storedFields.taxParts }}
                                blockedReason={exemptionBlockedReason({ source: taxSource, lines: invoiceData.lineItems, customerId: invoiceData.customerId, sourceCustomerId: taxSource?.customerId, word: 'invoice' })}
                                onFresh={(data) => {
                                    setTaxSource((prev) => overlayFields(prev, data, EXEMPTION_CHANGE_FIELDS));
                                    setInvoiceData((prev) => ({ ...overlayFields(prev, data, ['subtotal', 'taxAmount', 'total', 'taxParts', 'totalsEngineVersion', 'taxExemptionApplied', 'amountPaid', 'balanceDue']), lineItems: (data.lineItems || prev.lineItems).map((l) => ({ ...defaultLineItem, ...l })) }));
                                }} />
                            <SetAllTaxStrip lines={invoiceData.lineItems} onLinesChange={(next) => setInvoiceData((prev) => ({ ...prev, lineItems: next }))}
                                ctx={docTax.ctx} taxCtx={docTax.taxCtx} canPick={docTax.taxPick.canPick && !moneyLocked} fieldMarks={docTax.fieldMarks}
                                id="ifm-tax-all" selectClassName="p-1.5 border rounded-md text-xs bg-white min-w-[170px]" />
                        </div>
                    )}

                    {/* invlock-9f1a7d53 -- money-lock banner. Shown once a payment has been recorded
                        on this invoice; the amounts are frozen while everything else stays editable. */}
                    {moneyLocked && (
                        <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>{isWrittenOffInvoice /* s0bwoff-f74fb923 */
                                ? "This invoice was written off as bad debt, so its amounts and status are locked. You can still edit notes, photos, and details."
                                : "A payment has been recorded on this invoice, so the amounts are locked. You can still edit notes, photos, and details. To change what's owed, issue a credit note (coming soon)."}</span>
                        </div>
                    )}

                    <div className="space-y-3">
                        {invoiceData.lineItems.map((item, index) => (
                            <div key={index} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 relative group transition-all hover:shadow-md">
                                <button
                                    onClick={() => removeLineItem(index)}
                                    disabled={moneyLocked} /* invlock-7a5f2c93 -- frozen array: no removing lines on a paid invoice */
                                    className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors disabled:text-gray-200 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                                >
                                    <Trash2 size={16} />
                                </button>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
                                    {/* Catalog Select */}
                                    <div className="col-span-12 md:col-span-3">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Catalog Item</label>
                                        <div className="relative">
                                            <Search size={14} className="absolute left-2.5 top-3 text-gray-400" />
                                            <select
                                                value={item.serviceId}
                                                onChange={(e) => pickService(index, e.target.value)}
                                                disabled={moneyLocked} /* invlock-1b6e9a40 */
                                                className="w-full pl-8 p-2 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                            >
                                                <option value="">Custom Entry</option>
                                                {pickerItems(services, item.serviceId).map(s => <option key={s.id} value={s.id}>{s.pickerLabel}</option>)/* catscreen-7a2c4e91 */}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Name & Desc */}
                                    <div className="col-span-12 md:col-span-5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Item</label>
                                        <input
                                            name="name"
                                            value={item.name}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            placeholder="Item Name"
                                            disabled={moneyLocked || !!item.serviceId} /* invlock-0c3f8d75 */
                                            className={`w-full p-2 border rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none disabled:cursor-not-allowed ${(item.serviceId || moneyLocked) ? 'bg-gray-100 text-gray-600' : ''}`}
                                        />
                                        <LineDescriptionInput
                                            name="description"
                                            value={item.description}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            disabled={moneyLocked} /* invlock-e29a4c61 */
                                            labelClassName="text-[10px] font-bold text-gray-400 uppercase mb-1 block"
                                        />
                                    </div>

                                    {/* Qty & Price */}
                                    <div className="col-span-6 md:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Qty</label>
                                        <input
                                            type="number"
                                            name="quantity"
                                            value={item.quantity}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            disabled={moneyLocked} /* invlock-a4d70b38 */
                                            className="w-full p-2 border rounded-lg text-center font-semibold text-gray-700 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                    <div className="col-span-6 md:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Price</label>
                                        <div className="relative">
                                            <span className="absolute left-2 top-2 text-gray-400 text-xs">$</span>
                                            <input
                                                type="number"
                                                name="price"
                                                value={item.price}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                disabled={moneyLocked} /* invlock-f81c5e26 */
                                                className="w-full p-2 pl-5 border rounded-lg text-right font-semibold text-gray-700 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Item Options */}
                                <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap gap-4 items-center">
                                     <div className="flex items-center gap-2">
                                        <div className={`p-1 rounded ${item.type === 'Material' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                            {item.type === 'Material' ? <Package size={14} /> : <Wrench size={14} />}
                                        </div>
                                        <select
                                            name="type"
                                            value={item.type || 'Service'}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            disabled={moneyLocked} /* invlock-b3609fa2 */
                                            className="text-xs border-0 bg-transparent font-medium text-gray-700 focus:ring-0 cursor-pointer hover:bg-gray-100 rounded px-1 disabled:cursor-not-allowed disabled:text-gray-400"
                                        >
                                            <option value="Service">Service</option>
                                            <option value="Material">Material</option>
                                        </select>
                                    </div>

                                    {!item.serviceId && item.name && (
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="addToCatalog"
                                                id={`addToCatalog-${index}`}
                                                checked={item.addToCatalog}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                disabled={moneyLocked} /* invlock-c7d21e94 */
                                                className="h-3 w-3 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:cursor-not-allowed"
                                            />
                                            <label htmlFor={`addToCatalog-${index}`} className="ml-1 text-xs text-green-700 font-bold cursor-pointer flex items-center gap-1 select-none">
                                                <Save size={12} /> Save
                                            </label>
                                        </div>
                                    )}

                                    {docTax.ctx ? (
                                        // taxlines5-a4c81e37 — one Tax dropdown (codes, "No tax (exempt)"), locked with the amounts.
                                        <div className="flex flex-wrap items-center ml-auto gap-2">
                                            <FieldTaxChip line={item} show={docTax.taxPick.showFieldMarker} />
                                            <div>
                                                <LineTaxPicker line={item} ctx={docTax.ctx} taxCtx={docTax.taxCtx} canPick={docTax.taxPick.canPick} disabled={moneyLocked}
                                                    onPick={(next) => setInvoiceData((prev) => ({ ...prev, lineItems: prev.lineItems.map((l, j) => (j === index ? next : l)) }))}
                                                    id={`ifm-tax-${index}`} srLabel={`Tax for line ${index + 1}`}
                                                    selectClassName="p-1 border rounded-md text-xs w-40 bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" textClassName="text-xs text-gray-700" />
                                            </div>
                                        </div>
                                    ) : (<>
                                    {!item.taxExempt && (
                                        <select
                                            name="taxCodeId"
                                            value={item.taxCodeId || ''}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            className="p-1 border rounded-md text-xs w-24 bg-white ml-auto disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                            disabled={moneyLocked || isLoadingTax} /* invlock-d5a8f312 */
                                        >
                                            <option value="">Default Tax</option>
                                            {taxOptions.map(code => (
                                                <option key={code.Id} value={code.Id}>{code.Name}</option>
                                            ))}
                                        </select>
                                    )}
                                    
                                    <div className="flex items-center ml-auto md:ml-2">
                                        <input
                                            type="checkbox"
                                            name="taxExempt"
                                            id={`taxExempt-${index}`}
                                            checked={item.taxExempt}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            disabled={moneyLocked} /* invlock-e6b4907c */
                                            className="h-3 w-3 text-gray-500 border-gray-300 rounded disabled:cursor-not-allowed"
                                        />
                                        <label htmlFor={`taxExempt-${index}`} className="ml-1 text-xs text-gray-500 select-none">
                                            Tax Exempt
                                        </label>
                                    </div>
                                    </>)}
                                </div>
                            </div>
                        ))}
                    </div>
                    {docTax.totals && (
                        // taxlines5-a4c81e37 — Subtotal, one row per tax part ("GST 5%", "GST 5% — exempt #…"), Total.
                        <div className="mt-4 flex flex-col items-end gap-2">
                            <div className="w-full md:w-72 space-y-1 p-3 bg-gray-50 rounded-xl border border-gray-200 text-sm" data-testid="ifm-tax-rows">
                                <p className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatCurrency(calculations.subtotal)}</span></p>
                                {(moneyLocked ? [] : docTax.totals.taxParts).length ? docTax.totals.taxParts.map((p) => (
                                    <p key={p.key} className="flex justify-between text-gray-600"><span>{onModeRowLabel(p)}</span><span>{formatCurrency(p.amount)}</span></p>
                                )) : <p className="flex justify-between text-gray-600"><span>Tax</span><span>{formatCurrency(calculations.taxAmount)}</span></p>}
                                <p className="flex justify-between font-bold text-gray-800 border-t pt-1"><span>Total</span><span>{formatCurrency(calculations.total)}</span></p>
                            </div>
                            {!moneyLocked && <TaxChangeNote text={docTax.taxNote} className="w-full md:w-96" />}
                        </div>
                    )}

                    <div className="mt-6">
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Work Performed <span className="normal-case font-normal text-gray-400">(shown on invoice)</span></label>
                        <textarea 
                            name="workPerformed" 
                            value={invoiceData.workPerformed} 
                            onChange={handleChange} 
                            rows="3" 
                            className="w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Describe the work completed — visible to the customer on the invoice..."
                        />
                    </div>

                    {/* s1-wpfirst-4e9c1b73 — SEAM 1. What the customer will actually read,
                        shown where the operator is typing it. The Work Performed text is
                        rendered VERBATIM because it is typed right here — this is exact, not
                        an approximation. Included notes are COUNTED, never quoted: this modal
                        holds only 80-char previews of them (noteInclusions previewText) and no
                        signed photo URLs, so quoting would show a truncation the customer never
                        sees. The faithful full render, with photos, is on the invoice's own
                        page, which is also where the Send button lives.
                        KNOWN GAP: when editing an EXISTING invoice, noteGroups is empty (it is
                        only seeded on the job-generate path), so the note count line does not
                        render. The panel never claims there are no notes — it is simply silent
                        about them. Closing that needs the getOperatorInvoiceNotes callable,
                        which requires a persisted invoice doc; this modal only pre-mints an id
                        (ifm-notes-mint-2b8d5e6f). Deliberately left for a later seam. */}
                    {(invoiceData.workPerformed?.trim() || noteGroups.length > 0) && (() => {
                        const includedNotes = noteGroups
                            .flatMap((g) => g.notes)
                            .filter((n) => !excludedNoteIds.has(n.id));
                        const withPhotos = includedNotes.filter((n) => n.photoCount > 0).length;
                        return (
                            <div className="mt-3 border border-gray-200 bg-gray-50 rounded-xl p-3">
                                <p className="text-xs font-bold text-gray-500 uppercase mb-1">What the customer will read</p>
                                <p className="text-xs text-gray-400 mb-2">On the invoice PDF, the emailed invoice and the customer portal, under &ldquo;Notes from your service provider&rdquo;.</p>
                                {invoiceData.workPerformed?.trim() ? (
                                    <p className="text-sm text-gray-700 whitespace-pre-line">{invoiceData.workPerformed}</p>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Nothing typed in Work Performed yet.</p>
                                )}
                                {includedNotes.length > 0 && (
                                    <p className="text-xs text-gray-500 mt-2">
                                        Then {includedNotes.length} included note{includedNotes.length !== 1 ? 's' : ''}
                                        {withPhotos > 0 ? ` (${withPhotos} with photos)` : ''}, ticked below.
                                    </p>
                                )}
                            </div>
                        );
                    })()}

                    {/* r1-3f2-ifm-render-6b9a3f42 — note inclusion picker for the single
                        job-to-invoice path. Hidden unless the job-generate seed attached
                        customer-facing note groups; showJobLabels off since there is one job. */}
                    {noteGroups.length > 0 && (
                        <div className="mt-4">
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Notes to include on this invoice</label>
                            <NoteInclusionPicker
                                groups={noteGroups}
                                excludedNoteIds={excludedNoteIds}
                                onToggleNote={toggleNoteExcluded}
                                showJobLabels={false}
                            />
                        </div>
                    )}

                    <div className="mt-4">
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Internal Notes</label>
                        <textarea 
                            name="notes" 
                            value={invoiceData.notes} 
                            onChange={handleChange} 
                            rows="2" 
                            className="w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Add memo..."
                        />
                    </div>

                    {/* ifm-notes-strip-5e1a7c3f — Notes & Media strip in the create/edit
                        modal (mirrors QuoteFormModal + InvoiceDetailsView). Present BEFORE
                        the first save via the pre-minted id, so an invoice made from a
                        customer profile can carry photos while it is being built. Bound to
                        the job-aware key resolved above; allowFlagging off (internal). */}
                    {notesAttachedKey && (
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => setNotesExpanded(prev => !prev)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <MessageSquare size={16} /> Notes &amp; Media
                                    <span className="text-xs font-normal text-gray-400">({invoiceNotes.length})</span>
                                </span>
                                {notesExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                            </button>
                            {notesExpanded && (
                                <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                                    <p className="text-xs text-gray-500">
                                        {notesJobId
                                            ? "Notes and photos are saved to this job's record."
                                            : 'Notes and photos are saved to this invoice.'}
                                    </p>
                                    <NoteComposer
                                        ref={noteComposerRef}
                                        attachedTo={notesAttachedTo}
                                        orgId={orgId}
                                        currentUserProfile={currentUserProfile}
                                        allowFlagging={false}
                                        onDirtyChange={setComposerDirty}
                                        placeholder={notesJobId ? 'Add a note or photo about this job…' : 'Add a note or photo about this invoice…'}
                                    />
                                    <div className="space-y-2 pt-1">
                                        {invoiceNotes.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                                        ) : (
                                            /* wppromote-6a3f1e94 — unconditional: this modal is mounted only
                                               behind MANAGE_FINANCIALS (CrmApp openInvoiceModal + the mount
                                               guard) and its Work Performed textarea has no read-only mode
                                               (moneyLocked disables line items and totals, not this box). This
                                               is also the convert screen, so the unconditional prop is what
                                               puts Promote at the post-job, pre-invoice review moment. */
                                            invoiceNotes.map(note => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    currentUserProfile={currentUserProfile}
                                                    orgId={orgId}
                                                    onPromote={handlePromoteNote}
                                                    promotePending={pendingPromotions.includes(note.id)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* --- FOOTER --- */}
                <div className="p-4 border-t bg-white flex flex-col md:flex-row justify-between items-center gap-4 flex-shrink-0">
                    <div className="w-full md:w-auto flex justify-between md:block text-right md:text-left">
                        {error && <p className="text-red-500 text-xs mb-1">{error}</p>}
                        <div className="text-xs text-gray-500">Total Due</div>
                        <div className="text-2xl font-bold text-blue-600 leading-none">
                            {formatCurrency(calculations.total)}
                        </div>
                    </div>
                    
                    <div className="flex gap-3 w-full md:w-auto">
                        <button onClick={handleGuardedClose} className="flex-1 md:flex-none py-3 px-6 rounded-xl text-gray-600 font-medium hover:bg-gray-100 transition-colors"> {/* ifm-notes-close-6f2b8d4a */}
                            Cancel
                        </button>
                        <button 
                            onClick={() => handleSave()} 
                            disabled={isSaving}
                            className="flex-1 md:flex-none py-3 px-8 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isSaving ? <Loader2 size={18} className="animate-spin"/> : (recordPaymentOnSave ? 'Save & Record Payment' : 'Save Invoice') /* h1s6-8b2f4d19 */}
                        </button>
                    </div>
                </div>

                {taxConfirm && (
                    // taxlines5-a4c81e37 — an older invoice whose tax this save changes.
                    <ConfirmationModal
                        isOpen={true}
                        onClose={() => setTaxConfirm(false)}
                        onConfirm={() => { setTaxConfirm(false); handleSave(true); }}
                        title={taxChangeTitle('invoice')}
                        message={docTax.taxNote}
                        confirmText="Save"
                    />
                )}
            </div>
        </div>
    );
};

export default InvoiceFormModal;